import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { DefaultTeamRig } from '@app/cem/core-data/project-team-rigs/columns';
import { DefaultTeamsData } from '@app/cem/core-data/project-team/columns';
import { PlexEnvironment } from '@app/env';
import { WrappedHttpErrorResponse } from '@app/errors/wrapped-error';
import { PadConstructionProjectSubTypeId, ProjectTypeId } from '@app/shared/data/project-enums';
import { IProjectAssetMapping } from '@app/shared/interfaces';
import { DefaultProjectTeamMaintenanceView } from '@app/shared/models/default-project-team-maintenance-view';
import { PlanningTaskStatus } from '@app/shared/models/planning-task-status';
import { ProjectsView } from '@app/shared/models/projects-view';
import { AssetMappingConfiguration } from 'app/cem/project/models/asset-mapping-configuration';
import { ErrorService } from 'app/common/services/error.service';
import { Asset, AssetTeam, AssetType, DefaultProjectTeam, DrillDesign, Employee, EquationType, Function, ItemRateTableName, PhaseStepType, Pipeline, Project, ProjectAssetMapping, ProjectEstimate, TeamRole } from 'app/shared/models';
import { throwError as observableThrowError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ProjectCreate } from '../models/project-create';
import { ProjectDefaults } from '../models/project-defaults';
import { ProjectDrillGroupMapping } from '../models/project-drill-group-mapping';
import { ProjectPlanningData } from '../models/project-planning-data';
import { SurfaceDevelopmentPlanningData } from '../models/sd-planning-data';

interface IODataResult<T> {
  value: Array<T>;
  count: number;
}

@Injectable({ providedIn: 'root' })
export class GeneralProjectService {
  private http = inject(HttpClient);
  private errorService = inject(ErrorService);
  public environment = inject(PlexEnvironment);

  private headers = new HttpHeaders({ 'Content-Type': 'application/json' });

  _Cache = new Map<string, any>();

  createProjectWellView(projectId: number) {
    let queryString = `${this.environment.apiUrl}Integrations/ProjectAfe/ProjectChange/createwellviewproject/${projectId}`;
    return this.http.post(queryString, {}, { headers: this.headers }).pipe(
      catchError(error => this.handleErrorObservable(error, 'Error creating well view project.')));
  }

  refreshProjectApprovalMatrix() {
    let queryString = `${this.environment.apiUrl}ProjectApprovalMatrix/`;
    return this.http.post(queryString, {}, { headers: this.headers }).pipe(
      catchError(error => this.handleErrorObservable(error, 'Error while refreshing the AFE approval paths.')));
  }

  async postBulkProjectChanges(changes: { ProjectId: number; ProjectStatus: string; }[], ccrValue: string): Promise<any> {
    let queryString = `${this.environment.apiUrl}Projects/Bulk`;

    let data = { Changes: changes, CcrValue: ccrValue };

    return this.http.put(queryString, data, { headers: this.headers }).pipe(
      catchError(error => this.handleErrorObservable(error, 'Error Posting Bulk Project Status Changes'))).toPromise();
  }

  getAssetMapped(assetId: number): Promise<any[]> {
    let queryString = `${this.environment.apiUrl}AssetsProjectAssetMappingView?$filter=AssetId eq ${assetId}`;
    return this.get(queryString, 'Error getting asset', false);
  }

  get<T>(url: string, errorResult: string, useCache = true): Promise<T[]> {
    if (useCache && this._Cache.has(url)) {
      return Promise.resolve(this._Cache.get(url));
    }

    let response = this.http.get<IODataResult<T>>(url).pipe(
      map<IODataResult<T>, T[]>(response => response.value),
      catchError(error => this.handleErrorObservable(error, errorResult))).toPromise();

    if (useCache) {
      response.then(x => {
        this._Cache.set(url, x);
      })
    }

    return response;
  }

  getone<T>(url: string, errorResult: string, useCache = true): Promise<T> {
    if (useCache && this._Cache.has(url)) {
      return Promise.resolve(this._Cache.get(url));
    }

    let response = this.http.get<IODataResult<T>>(url).pipe(
      map((response: any) => response as T),
      catchError(error => this.handleErrorObservable(error, errorResult))).toPromise();

    if (useCache) {
      response.then(x => {
        this._Cache.set(url, x);
      })
    }

    return response;
  }

  getAssetMappingConfiguration(projetTypeId: number): Promise<AssetMappingConfiguration[]> {
    let assetMappingConfigExpand = '&$expand=ProjectType($select=ProjectTypeId,Description),AssetSuperType($select=AssetSuperTypeId,Description),Module($select=ModuleId,Name,Description)';
    let queryString = `${this.environment.apiUrl}AssetMappingConfigurations?&$filter=ProjectTypeId eq ${projetTypeId} ${assetMappingConfigExpand}`;

    return this.get(queryString, 'Error getting asset well-mapping configuration');
  }

  getAssets(assetTeamId: number, assetTypeId: number, filterValue: string | number, filterType: 'AssetId' | 'AssetName', projectTypeId?: number, project?: Project): Promise<Asset[]> {
    if (assetTypeId == undefined) {
      throw new Error('AssetTypeId must be provided but is not when calling AssetsProjectAssetMappingView');
    }
    if (assetTeamId == undefined) {
      throw new Error('assetTeamId must be provided but is not when calling AssetsProjectAssetMappingView');
    }
    if (filterValue == undefined) {
      throw new Error(`${filterType} must be provided but is not when calling AssetsProjectAssetMappingView`);
    }

    let select = `AssetsProjectAssetMappingView?$top=20`;
    let filter = `&$filter=AssetTypeId eq ${assetTypeId} and AssetTeamId eq ${assetTeamId} and IsActive eq true`;

    if (filterType === 'AssetId') {
      filter += ` and contains(cast(AssetId,'Edm.String'),'${filterValue}')`;
    } else {
      filter += ` and contains(AssetName,'${filterValue}')`;
    }

    if (projectTypeId) {
      filter += ` and not contains(ProjectTypes, '${projectTypeId}')`;
    }

    // Exclude tank battery assets that are tied to other pad construction projects
    if (project && project.ProjectSubTypeId === PadConstructionProjectSubTypeId.TankBattery) {
      filter += ` and (not contains(ProjectSubTypes, '${project.ProjectSubTypeId}') or contains(ProjectIds,'${project.ProjectId}'))`;
    }
    let queryString = `${this.environment.apiUrl}${select}${filter}`;
    return this.get(queryString, 'Error getting assets with filter', false);
  }

  getAssetTeams(): Promise<AssetTeam[]> {
    let queryString = `${this.environment.apiUrl}AssetTeams?$select=AssetTeamId,AssetTeamCode`;
    return this.get(queryString, 'Error getting asset teams');
  }

  geProjectTeamsMaintenanceView(): Promise<DefaultProjectTeamMaintenanceView[]> {
    let queryString = `${this.environment.apiUrl}DefaultProjectTeamMaintenanceView`;
    return this.get(queryString, 'Error getting project teams', false);
  }

  addUpdateDefaultTeams(data: DefaultTeamsData[]) {
    let queryString = `${this.environment.apiUrl}DefaultProjectTeamMaintenanceView/addUpdateDefaultTeams`;
    return this.http.post(queryString, data, { headers: this.headers }).pipe(
      catchError(error => this.handleErrorObservable(error, 'Error saving default Teams')));
  }

  getFunctions(): Promise<Function[]> {
    let queryString = `${this.environment.apiUrl}Functions?$select=FunctionId,Description`;
    return this.get(queryString, 'Error getting functions');
  }

  getAssetTypes(): Promise<AssetType[]> {
    let queryString = `${this.environment.apiUrl}AssetTypes?$select=AssetTypeId,Description,AssetSuperTypeId`;
    return this.get(queryString, 'Error getting asset types');
  }

  getDefaultProjecTeams(assetTeamId: number, functionId: number): Promise<DefaultProjectTeam[]> {
    let orderBy = '&$orderby=Description asc';
    let queryString = `${this.environment.apiUrl}DefaultProjectTeams?&$filter=AssetTeamId eq ${assetTeamId}`;
    if (functionId > 0)
      queryString = `${queryString} and FunctionId eq ${functionId}`;
    queryString = `${queryString}${orderBy}`;
    return this.get(queryString, 'Error getting default project teams');
  }

  getDrillDesigns(): Promise<DrillDesign[]> {
    let queryString = `${this.environment.apiUrl}DrillDesigns?$select=DrillDesignId,Name`;
    return this.get(queryString, 'Error getting drill designs');
  }


  getEmployees(): Promise<Employee[]> {
    let queryString = `${this.environment.apiUrl}Employees?$select=EmployeeId,PreferredFirstName,LastName,MiddleName,JobStatus,WorkerType,JobTitle,EmployeeUID`;
    return this.get(queryString, 'Error getting employees');
  }

  getItemRateTableNames(): Promise<ItemRateTableName[]> {
    let queryString = `${this.environment.apiUrl}ItemRateTableNames?$select=ItemRateTableNameId,TableName,OwnerId`;
    return this.get(queryString, 'Error getting item rate table names');
  }

  getItemRatesData(itemTableNameId: number, page: number, pageSize: number): Promise<any> {
    let queryString = `${this.environment.apiUrl}rate/itemRatesData/${itemTableNameId}/${page}/${pageSize}`;
    return this.get(queryString, 'Error getting item rate data');
  }

  getPhaseStepTypes(): Promise<PhaseStepType[]> {
    let queryString = `${this.environment.apiUrl}PhaseStepTypes?$select=PhaseStepTypeId,Phase,Step`;
    return this.get(queryString, 'Error getting phase step types');
  }

  getEquationTypes(): Promise<EquationType[]> {
    let queryString = `${this.environment.apiUrl}EquationTypes?$select=EquationTypeId,Description`;
    return this.get(queryString, 'Error getting equation types');
  }

  getPipelines(assetTeamId: number): Promise<Pipeline[]> {
    let pipelineSelect = '$select=PipelineId';
    let pipelineExpand = '&$expand=Asset($select=AssetId,AssetName,AssetTypeId,AssetTeamId)';
    let pipelineFilter = '&$filter=Asset/AssetTeamId eq ';
    let queryString = `${this.environment.apiUrl}Pipelines?${pipelineSelect}${pipelineExpand}${pipelineFilter}${assetTeamId}`;
    return this.get(queryString, 'Error getting pipelines');
  }

  getProject(projectId: number): Promise<Project[]> {
    let queryString = `${this.environment.apiUrl}Projects?&$filter=ProjectId eq ${projectId}`;
    return this.get(queryString, 'Error getting project', false);
  }

  getProjectView(projectId: number): Promise<ProjectsView> {
    let queryString = `${this.environment.apiUrl}ProjectsView(${projectId})?$select=ProjectId,AfeNumber,Name,FunctionId,Function,AssetTeamId,AssetTeamCode,ProjectTypeId,ProjectType,ProjectSubTypeId,ProjectSubType,ProjectStatus,ProjectStatusId,IsProjectTeamSync,ScenarioId,DefaultProjectTeam,DefaultProjectTeamId,CreatedBy,DivisionId,Division,StartDate,NeedByDate,AfeApprovalDate,ProjectManagerId,ProjectManagerName,ProjectControlsAnalystId,ProjectControlsAnalystName,ProjectEngineerId,ProjectEngineerName,ProgramManagerId,ProgramManagerName,ProjectTechId,ProjectTechName,CostCenter,BudgetCategoryId,BudgetCategoryName`;
    return this.getone(queryString, 'Error getting project', false);
  }

  getProjectTeam(projectId: number): Promise<Project> {
    let queryString = `${this.environment.apiUrl}Projects(${projectId})?$select=ProjectId,%20DefaultProjectTeamId,IsProjectTeamSync`;
    return this.getone(queryString, 'Error getting project team', false);
  }

  getProjectAssetMapping(projectId: number): Promise<ProjectAssetMapping[]> {
    let queryString = `${this.environment.apiUrl}ProjectAssetMappings?&$filter=ProjectId eq ${projectId}`;
    return this.get(queryString, 'Error getting project asset mapping', false);
  }

  getProjectAssetMappingBySuperType(filterString: string): Promise<ProjectAssetMapping[]> {
    let queryString = `${this.environment.apiUrl}ProjectAssetMappings?&$filter=${filterString}`;
    return this.get(queryString, 'Error getting project asset mapping', false);
  }

  getProjectEstimateAFE(projectId: number): Promise<ProjectEstimate[]> {
    let select = '?$select=ProjectEstimateId,ProjectId,RecordApproverId,RecordApprovalDate,ProjectAttachmentTypeId';
    let orderBy = '&$orderby=EstimateVersion desc';
    let filter = `&$filter=ProjectId eq ${projectId} and (ProjectAttachmentTypeId eq 3 or ProjectAttachmentTypeId eq 1) and RecordStatus ne 'Rejected' and RecordApproverId gt 0`;
    let top = '&$top=1';
    let queryString = `${this.environment.apiUrl}ProjectEstimates${select}${orderBy}${filter}${top}`;
    return this.get(queryString, 'Error getting project estimate AFE approver', false);
  }

  getTeamRoles(): Promise<TeamRole[]> {
    let queryString = `${this.environment.apiUrl}TeamRoles`;
    return this.get(queryString, 'Error getting team roles');
  }

  getWellAssetIdContains(scenarioId: number, assetTeamId: number, assetTypeId: number, assetId: number, projectTypeId?: number): Promise<any[]> {
    let select = 'WellsProjectAssetMappingView?';
    let filter = `$top=20&$filter=ScenarioId eq ${scenarioId} and AssetTypeId eq ${assetTypeId} and AssetTeamId eq ${assetTeamId} and contains(cast(AssetId,'Edm.String'),'${assetId}')`;
    if (projectTypeId) filter += ` and not contains(ProjectTypes, '${projectTypeId}')`;
    let queryString = `${this.environment.apiUrl}${select}${filter}`;
    return this.get(queryString, 'Error getting wells mapping containing AssetId filter', false);
  }

  getWellAssetNameContains(scenarioId: number, assetTeamId: number, assetTypeId: number, assetName: string, projectTypeId?: number): Promise<any[]> {
    let select = 'WellsProjectAssetMappingView?';
    let filter = `$top=20&$filter=ScenarioId eq ${scenarioId} and AssetTypeId eq ${assetTypeId} and AssetTeamId eq ${assetTeamId} and contains(cast(AssetName,'Edm.String'),'${assetName}')`;
    if (projectTypeId) filter += ` and not contains(ProjectTypes, '${projectTypeId}')`;
    let queryString = `${this.environment.apiUrl}${select}${filter}`;
    return this.get(queryString, 'Error getting wells mapping containing AssetId filter', false);
  }

  getWellAssetMapped(assetId: number, scenarioId: number): Promise<any[]> {
    let queryString = `${this.environment.apiUrl}WellsProjectAssetMappingView?$filter=AssetId eq ${assetId} and ScenarioId eq ${scenarioId}`;
    return this.get(queryString, 'Error getting asset', false);
  }

  getWellOwidContains(scenarioId: number, assetTeamId: number, assetTypeId: number, owid: number, projectTypeId?: number): Promise<any[]> {
    let select = 'WellsProjectAssetMappingView?';
    let filter = `$top=20&$filter=ScenarioId eq ${scenarioId} and AssetTypeId eq ${assetTypeId} and AssetTeamId eq ${assetTeamId} and contains(cast(OWID,'Edm.String'),'${owid}')`;
    if (projectTypeId) filter += ` and not contains(ProjectTypes, '${projectTypeId}')`;
    let queryString = `${this.environment.apiUrl}${select}${filter}`;
    return this.get(queryString, 'Error getting wells mapping containing Owid filter', false);
  }

  getWellSsnContains(scenarioId: number, assetTeamId: number, assetTypeId: number, ssn: string, projectTypeId?: number): Promise<any[]> {
    let select = 'WellsProjectAssetMappingView?';
    let filter = `$top=20&$filter=ScenarioId eq ${scenarioId} and AssetTypeId eq ${assetTypeId} and AssetTeamId eq ${assetTeamId} and contains(Ssn,'${ssn}')`;
    if (projectTypeId) filter += ` and not contains(ProjectTypes, '${projectTypeId}')`;
    let queryString = `${this.environment.apiUrl}${select}${filter}`;
    return this.get(queryString, 'Error getting wells mapping containing ssn filter', false);
  }

  postProject(projectHeader: ProjectCreate, useNewEngine: boolean, createPostStimProject: boolean = false, createWellsProjectsFromGroup: boolean = false): Promise<Project[]> {
    let queryString = `${this.environment.apiUrl}projectheader?useNewEngine=${useNewEngine}&createPostStimProject=${createPostStimProject}&createWellsProjectsFromGroup=${createWellsProjectsFromGroup}`;
    return this.http.post<Project[]>(queryString, projectHeader, { headers: this.headers })
      .toPromise()
      .catch(error => this.handleErrorPromise(error, 'Error creating new project'));
  }

  postProjectWellReassignment(wellReassignment: { CurrentWellId: number, NewWellId: number, ProjectId: number, ScenarioId: number }) {
    let queryString = `${this.environment.apiUrl}ProjectsCustom/ProjectWellReassignment`;
    return this.http.post(queryString, wellReassignment, { headers: this.headers }).pipe(
      catchError(error => this.handleErrorObservable(error, 'Error reassigning well to project')));
  }

  unAssignProjectAssets(wells: Array<number>[], projectTypeId: number) {
    let queryString = `${this.environment.apiUrl}ProjectsCustom/UnassignProjectAssets?projectTypeId=${projectTypeId}`;
    return this.http.post(queryString, wells, { headers: this.headers }).pipe(
      catchError(error => this.handleErrorObservable(error, 'Error reassigning well to project')));
  }

  deleteProjectAssetMappings(projectAssetMappings: IProjectAssetMapping[]): Promise<any> {
    let queryString = `${this.environment.apiUrl}ProjectAssetMappingsBatch`;
    let idObjects = projectAssetMappings.map(item => { return { ProjectId: item.ProjectId, ProjectAssetMappingId: item.ProjectAssetMappingId * -1, AssetId: item.AssetId, PercentAllocation: item.PercentAllocation } });
    return this.http.patch(queryString, idObjects).pipe(
      catchError(error => this.handleErrorObservable(error, 'Error deleting project asset mappings'))).toPromise();
  }

  getProjectDefaults(projectTypeId: number, scenarioId: number, wellId: number, drillGroupId: number): Promise<ProjectDefaults> {
    let queryString = `${this.environment.apiUrl}projectHeader/projectDefaults?projectTypeId=${projectTypeId}&scenarioId=${scenarioId}`;
    if (wellId > 0)
      queryString = `${queryString}&wellId=${wellId}`;
    else
      queryString = `${queryString}&drillGroupId=${drillGroupId}`;

    return this.getone(queryString, 'Error getting project defaults', false)
  }

  getProjectTeamRigDefaults(scenarioId: number): Promise<any> {
    let queryString = `${this.environment.apiUrl}projectteamsdata/getDefaultRigTeams?scenarioId=${scenarioId}`;
    return this.getone(queryString, 'Error getting project defaults');
  }

  getProjectTeamFracFleetDefaults(scenarioId: number): Promise<any> {
    let queryString = `${this.environment.apiUrl}projectteamsdata/getDefaultFracFleetTeams?scenarioId=${scenarioId}`;
    return this.getone(queryString, 'Error getting project defaults');
  }

  addUpdateDefaultRigTeams(data: DefaultTeamRig[]) {
    let queryString = `${this.environment.apiUrl}projectteamsdata/updateDefaultRigTeams`;
    return this.http.post(queryString, data, { headers: this.headers }).pipe(
      catchError(error => this.handleErrorObservable(error, 'Error saving default Rig Teams')));
  }

  addUpdateDefaultFracFleetTeams(data: DefaultTeamRig[]) {
    let queryString = `${this.environment.apiUrl}projectteamsdata/updateDefaultFracFleetTeams`;
    return this.http.post(queryString, data, { headers: this.headers }).pipe(
      catchError(error => this.handleErrorObservable(error, 'Error saving default FracFleet Teams')));
  }

  getProjectPlanningData(projectId: number, projectTypeId: number): Promise<ProjectPlanningData> {
    let queryString = ``;

    switch (projectTypeId) {
      case ProjectTypeId.TankBattery:
        queryString = `${this.environment.apiUrl}projectdata/getTankBatteryProjectPlanning/${projectId}`;
        break;
      case ProjectTypeId.SaltWaterDisposal:
        queryString = `${this.environment.apiUrl}projectdata/getSWDFacilityProjectPlanning/${projectId}`;
        break;
    }

    return this.getone(queryString, 'Error getting project event logs', false);
  }

  getSurfaceDevelopmentProjectPlanningData(projectId: number): Promise<SurfaceDevelopmentPlanningData> {
    let queryString = `${this.environment.apiUrl}projectdata/getSurfaceDevelopmentProjectPlanning/${projectId}`;
    return this.getone(queryString, 'Error getting project event logs', false);
  }

  getPlanningTaskStatuses(projectTypeId: number): Promise<PlanningTaskStatus[]> {
    let queryString = `${this.environment.apiUrl}PlanningTaskStatuses?$select=PlanningTaskStatusId,Description&$filter=ProjectTypeId eq ${projectTypeId}`;
    return this.get(queryString, 'Error getting planning task status');
  }

  getDrillGroups(projectTypeId: number, projectSubTypeId: number = null): Promise<ProjectDrillGroupMapping[]> {
    let url = `${this.environment.apiUrl}projectdata/getDrillGroups/${projectTypeId}`;
    if (projectSubTypeId != null) {
      url += `/${projectSubTypeId}`
    }
    return this.http.get(url).pipe(
      map((response: any) => response as ProjectDrillGroupMapping[]),
      catchError(error => this.handleErrorObservable(error, 'Error getting drill groups.'))).toPromise();
  }

  updateProjectPlanningTasks(data: ProjectPlanningData) {
    let queryString = `${this.environment.apiUrl}projectdata/updateProjectPlanningTasks`;
    return this.http.post(queryString, data, { headers: this.headers }).pipe(
      catchError(error => this.handleErrorObservable(error, 'Error saving project planning tasks.')));
  }

  private handleErrorObservable(error: Response | any, message: string) {
    this.errorService.error(message, error);
    return observableThrowError(new WrappedHttpErrorResponse(message, error));
  }

  private handleErrorPromise(error: Response | any, message: string) {
    this.errorService.error(message, error);
    return Promise.reject(error.message || error);
  }
}